import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { DD_SLASH_MMM_SLASH_YYYY_HH_COLON_MM } from 'constants/dateFormat';
import { VIEW_PATIENTS } from 'constants/userOperations';
import { isEmpty } from 'lodash/lang';
import moment from 'moment-timezone';
import { userHasAccessTo } from 'services/auth';

import { SitePatientApi } from '../../../../api';
import ModalBoxes from '../../../../common/feedback/ModalBoxes/ModalBoxes';
import { pendoTrackDefaultSortingChange } from '../../../../pendo/PendoUtils';
import { i18nt } from '../../../../root';
import { muiDataGridLocaleText } from '../../../../services/i18n/mui';
import { NSPP_PATIENT } from '../../../../services/i18n/namespaces';
import { useEuiFiltersState, withEuiFiltersProvider } from '../../../eui/EuiFiltersContext/EuiFiltersContext';
import { FRS_READY } from '../../../eui/EuiFiltersContext/reducer';

import FiltersSection from './FiltersSection/FiltersSection';
import AddOrEditPatientInfoModal from './PatientInfo/PatientInfoMainSection/AddOrEditPatientInfoModal/AddOrEditPatientInfoModal';
import { initialState } from './patientsListInitialState';
import useDataGrid from './useDataGrid';

import './Patients.scss';

function Patients() {
  const { t } = useTranslation();
  const onceLoaded = useRef(false);
  const alreadyLoaded = useRef(false);
  const [loading, setLoading] = useState(false);
  const { schema, statuses, ssu } = useEuiFiltersState();
  const [columns, gridData, setGridData] = useDataGrid();

  const requestObject = useMemo(
    function() {
      if (schema?.status !== FRS_READY || isEmpty(ssu)) {
        return;
      }
      const { studySites, selectedStudyId, selectedSiteMap, selectedSites } = ssu;
      const ssuIds = collectSsuIds(studySites, selectedStudyId, selectedSiteMap, selectedSites);
      alreadyLoaded.current = false;
      return {
        statuses: statuses.value.map(status => status.id),
        ssuIds
      };
    },
    [schema, statuses, ssu]
  );

  const loadTableData = useCallback(
    function(request) {
      setLoading(true);
      SitePatientApi.getPatients(request.ssuIds, request.statuses)
        .then(({ data }) => {
          data.forEach(row => {
            row.patientName = `${row.lastName}, ${row.firstName}${resolvePreferredName(row.preferredName)}`;
          });
          setGridData(data);
          alreadyLoaded.current = true;
        })
        .finally(() => setLoading(false));
    },
    [setGridData]
  );

  const onApply = useCallback(
    function() {
      if (requestObject) {
        loadTableData(requestObject);
      }
    },
    [loadTableData, requestObject]
  );

  useEffect(
    function() {
      if (onceLoaded.current || !requestObject) {
        return;
      }
      if (!isEmpty(requestObject.ssuIds)) {
        loadTableData(requestObject);
      }
      onceLoaded.current = true;
    },
    [loadTableData, requestObject]
  );

  const applyDisabled = alreadyLoaded.current || loading || isEmpty(requestObject?.ssuIds);

  return (
    <div className="eui-patients">
      <FiltersSection gridData={gridData} onApply={onApply} applyDisabled={applyDisabled} addPatient={addPatient} />
      <DataGridPremium
        initialState={initialState}
        getRowId={getRowId}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
            excelOptions: {
              disableToolbarButton: !userHasAccessTo(VIEW_PATIENTS),
              fileName: `Patients_${moment(new Date()).format(DD_SLASH_MMM_SLASH_YYYY_HH_COLON_MM)}`
            },
            quickFilterProps: {
              debounceMs: 500
            }
          }
        }}
        loading={loading}
        rowHeight={50}
        rows={gridData}
        columns={columns}
        disableRowSelectionOnClick
        localeText={muiDataGridLocaleText({ noRowsLabel: t('noRecordFound') })}
        disableRowGrouping
        disableAggregation
        disablePivoting
        onSortModelChange={(model = []) => {
          const mappedModel = model.map(e => ({
            id: e.field,
            desc: e.sort === 'desc'
          }));
          pendoTrackDefaultSortingChange(mappedModel);
        }}
        showToolbar
      />
    </div>
  );

  function addPatient() {
    const addPatientInfoModal = ModalBoxes.open({
      component: (
        <AddOrEditPatientInfoModal
          onSave={() => {
            onApply();
            addPatientInfoModal.close();
          }}
        />
      ),
      title: i18nt('addOrEdit.addPatientInfo', { ns: NSPP_PATIENT })
    });
  }
}

function getRowId({ uniqueIdentifier }) {
  return uniqueIdentifier;
}

function collectSsuIds(studySites, selectedStudyId, selectedSiteMap, selectedSites) {
  if (isEmpty(selectedSites) && !selectedStudyId) {
    return [];
  }
  if (!isEmpty(selectedSites) && selectedSites.length > 1 && !selectedStudyId) {
    return [];
  }

  return studySites.reduce(function(accumulator, { id, study, site }) {
    if (selectedStudyId === study.id && isEmpty(selectedSites)) {
      accumulator.push(id);
      return accumulator;
    }
    if (selectedSiteMap[site.id] && !selectedStudyId) {
      accumulator.push(id);
      return accumulator;
    }
    if (selectedSiteMap[site.id] && selectedStudyId === study.id) {
      accumulator.push(id);
      return accumulator;
    }
    return accumulator;
  }, []);
}

function resolvePreferredName(preferredName) {
  if (preferredName) {
    return ` (${preferredName})`;
  }
  return '';
}

export default withEuiFiltersProvider(Patients, { cachePrefix: 'PTSF' });
